// ForgetPassword.jsx
import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function ForgetPassword({
  pageTitle = "Forget Password | International Islamic University Chittagong",
  sendOtpUrl = "/api/auth/send-otp", // only API used for sending OTP
  resetPasswordUrl = "/api/auth/reset-password", // API for setting new password
  loginPath = "/login",
  otpLength = 6,
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState(null); // OTP generated by React

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  // OTP resend cooldown (seconds)
  const RESEND_COOLDOWN = 45;

  const validMobile = (m) => {
    const cleaned = m.replace(/\D/g, "");
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const startCountdown = () => {
    clearInterval(countdownRef.current);
    setCountdown(RESEND_COOLDOWN);
    countdownRef.current = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(countdownRef.current), []);

  // Generate local OTP and send via API for SMS
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!validMobile(mobile)) {
      setError("Please enter a valid mobile number.");
      return;
    }

    const otpCode = String(Math.floor(100000 + Math.random() * 900000)); // random 6-digit OTP
    setGeneratedOtp(otpCode);

    setLoading(true);
    try {
		const payload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			mobile: mobile,			
			otp: otpCode,
			purpose: "Forget Password",
		};
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		const res = await api.post("/forget-password-otp", payload);
		
		
		//const res = await axios.post(sendOtpUrl, { mobile, otp: otpCode }); // send to backend for SMS
		const data = res?.data ?? {};

		if (res?.data?.responseCode === "200" || res?.data?.responseCode === 200) {
			setMessage("OTP sent successfully to your mobile.");
			setStep(2);
			startCountdown();
		} else {
			throw new Error(res.data.responseMsg || "Failed to send OTP.");
		}
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  // Verify locally generated OTP
  const handleVerifyOtp = (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (otp.trim() === generatedOtp) {
      setMessage("OTP verified successfully. Please set your new password.");
      setStep(3);
    } else {
      setError("Invalid OTP. Please try again.");
    }
  };

  // Reset password via API
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Password and Confirm Password do not match.");
      return;
    }

    setLoading(true);
    try {
		const payload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			mobile: mobile,			
			user_password: password,			
		};
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		const res = await api.post("/password-reset", payload);
		
		if (res?.data?.responseCode === "200" || res?.data?.responseCode === 200) {
			setMessage("Password updated successfully! Redirecting to login...");
			setTimeout(() => navigate(loginPath), 1000);
		} else {
			throw new Error(res.data.responseMsg || "Failed to set password.");
		}
    } catch (err) {
		setError(err.response?.data?.message || err.message || "Reset failed.");
    } finally {
		setLoading(false);
    }
  };

  const formatSecs = (s) => (s > 0 ? `${s}s` : "");

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-6 relative overflow-hidden">
      {/* SVG Background */}
      <svg
        className="pointer-events-none absolute left-0 top-0 w-full h-full opacity-20"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 320"
      >
        <path
          fill="#60a5fa"
          fillOpacity="0.3"
          d="M0,160L48,176C96,192,192,224,288,229.3C384,235,480,213,576,181.3C672,149,768,107,864,128C960,149,1056,235,1152,261.3C1248,288,1344,256,1392,240L1440,224L1440,0L0,0Z"
        ></path>
      </svg>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md bg-white/80 backdrop-blur-md rounded-2xl border border-slate-200 shadow-xl p-8"
      >
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800">
            Forgot Password
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Reset your password securely via OTP verification
          </p>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm"
          >
            {error}
          </motion.div>
        )}

        {message && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg mb-4 text-sm"
          >
            {message}
          </motion.div>
        )}

        {/* Step 1: Enter Mobile */}
        {step === 1 && (
          <motion.form
            onSubmit={handleSendOtp}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Mobile Number
              </label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-sky-300 outline-none shadow-sm"
                placeholder="01XXXXXXXXX"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-sky-500 to-violet-600 text-white py-3 rounded-lg font-medium shadow-md hover:scale-[1.01] transition"
            >
              {loading ? "Sending OTP..." : "Generate OTP"}
            </button>
          </motion.form>
        )}

        {/* Step 2: Enter OTP */}
        {step === 2 && (
          <motion.form
            onSubmit={handleVerifyOtp}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Enter OTP
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\\D/g, "").slice(0, otpLength))
                }
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-center text-lg tracking-widest focus:ring-2 focus:ring-sky-300 outline-none shadow-sm"
                placeholder="Enter OTP"
                required
              />
            </div>
            <div className="flex justify-between items-center">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-sky-500 to-violet-600 text-white py-3 rounded-lg font-medium shadow-md"
              >
                Verify OTP
              </button>
              <button
                type="button"
                disabled={countdown > 0}
                onClick={handleSendOtp}
                className="ml-3 text-sm text-sky-600 hover:underline"
              >
                {countdown > 0 ? `Resend in ${formatSecs(countdown)}` : "Resend"}
              </button>
            </div>
          </motion.form>
        )}

        {/* Step 3: Reset Password */}
        {step === 3 && (
          <motion.form
            onSubmit={handleResetPassword}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-sky-300 outline-none shadow-sm"
                placeholder="Enter new password"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Confirm Password
              </label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-sky-300 outline-none shadow-sm"
                placeholder="Re-enter password"
                required
                minLength={6}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-sky-500 to-violet-600 text-white py-3 rounded-lg font-medium shadow-md hover:scale-[1.01] transition"
            >
              {loading ? "Updating..." : "Set Password"}
            </button>
          </motion.form>
        )}
      </motion.div>
    </div>
  );
}
